import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';

// Zeugnis direkt in der Blechbedarf-Liste (Sevi 14.10.2026: „wie früher in der Liste direkt zum Eintragen“).
// Zelle zeigt den Eintrag (ja / 3.1 / fehlt …) + 📜 je Zeugnis-PDF; Klick öffnet ein kleines Menü:
// Schnellwahl, freier Text, PDF hochladen/öffnen — ohne die Position zu öffnen.

export type ZeugnisArt = 'ok' | 'fehlt' | 'text' | 'leer';
export function zeugnisArt(v: string | null | undefined): ZeugnisArt {
  const t = (v ?? '').trim().toLowerCase();
  if (!t) return 'leer';
  if (/^(nein|n|fehlt|offen|-|–|kein)/.test(t)) return 'fehlt';
  if (/^(ja|j|x|✓|ok|vorhanden|da|liegt vor)\b|^(ja|x|✓)$|\b(2\.2|3\.1|3\.2)\b/.test(t)) return 'ok';
  return 'text';
}
const STIL: Record<ZeugnisArt, string> = {
  ok: 'bg-emerald-100 text-emerald-800',
  fehlt: 'bg-red-100 text-red-700',
  text: 'bg-slate-100 text-slate-700',
  leer: '',
};
const SCHNELL = [
  { wert: 'ja', label: '✓ ja' },
  { wert: '3.1', label: '3.1' },
  { wert: '3.2', label: '3.2' },
  { wert: '2.2', label: '2.2' },
  { wert: 'fehlt', label: '✗ fehlt' },
];

export function ZeugnisZelle({ id, zeugnis, angekommen, pdfs, onGeaendert }: {
  id: number; zeugnis: string | null; angekommen: boolean; pdfs: string[];
  onGeaendert: (neu: { zeugnis?: string | null; pdf?: boolean }) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [text, setText] = useState(zeugnis ?? '');
  const [busy, setBusy] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const art = zeugnisArt(zeugnis);

  useEffect(() => { if (!offen) setText(zeugnis ?? ''); }, [zeugnis, offen]);
  useEffect(() => {
    if (!offen) return;
    const zu = (e: Event) => {
      if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return;
      if (e.type === 'mousedown' && box.current?.contains(e.target as Node)) return;
      setOffen(false);
    };
    window.addEventListener('mousedown', zu);
    window.addEventListener('keydown', zu);
    window.addEventListener('resize', zu);
    document.querySelector('main')?.addEventListener('scroll', zu, { passive: true });
    return () => {
      window.removeEventListener('mousedown', zu); window.removeEventListener('keydown', zu); window.removeEventListener('resize', zu);
      document.querySelector('main')?.removeEventListener('scroll', zu);
    };
  }, [offen]);

  const oeffnen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = knopf.current?.getBoundingClientRect();
    if (r) setPos({ top: Math.min(r.bottom + 4, window.innerHeight - 230), left: Math.max(8, Math.min(r.left, window.innerWidth - 310)) });
    setOffen(o => !o);
  };
  const speichern = async (wert: string) => {
    setBusy(true);
    try {
      const v = wert.trim() || null;
      await api.blechbedarf.update(id, { zeugnis: v });
      onGeaendert({ zeugnis: v });
      setOffen(false);
    } catch (e) { alert('Speichern fehlgeschlagen: ' + (e as Error).message); }
    setBusy(false);
  };
  const hochladen = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const datei = f.name.startsWith('Zeugnis_') ? f : new File([f], `Zeugnis_${f.name}`, { type: f.type });
        await api.einkauf.dateiHochladen(id, datei, 'zeugnis');
      }
      // PDF da, aber noch nichts eingetragen → automatisch „ja“, damit die Spalte stimmt
      if (zeugnisArt(zeugnis) === 'leer' || zeugnisArt(zeugnis) === 'fehlt') {
        await api.blechbedarf.update(id, { zeugnis: 'ja' });
        onGeaendert({ zeugnis: 'ja', pdf: true });
      } else onGeaendert({ pdf: true });
      setOffen(false);
    } catch (e) {
      const msg = e instanceof ApiError && (e.body as { error?: string } | null)?.error;
      alert(`Upload fehlgeschlagen: ${msg || String(e)}`);
    }
    setBusy(false);
  };

  return (
    <div onClick={e => e.stopPropagation()} className="relative">
      <button ref={knopf} type="button" data-testid={`zeugnis-${id}`} onClick={oeffnen}
              title={zeugnis ? `Zeugnis: ${zeugnis}${pdfs.length ? ` · ${pdfs.length} PDF` : ''}` : 'Zeugnis eintragen'}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap max-w-[120px]
                ${art === 'leer'
                  ? (angekommen && pdfs.length === 0 ? 'border border-dashed border-amber-400 text-amber-700 bg-amber-50' : 'text-slate-300 hover:text-slate-600 hover:bg-white')
                  : STIL[art]}`}>
        {art === 'leer' ? (angekommen && pdfs.length === 0 ? '? fehlt' : '＋') : <span className="truncate">{art === 'ok' && !/^✓/.test(zeugnis ?? '') ? '✓ ' : ''}{zeugnis}</span>}
        {pdfs.length > 0 && <span aria-label={`${pdfs.length} Zeugnis-PDF`}>📜{pdfs.length > 1 ? pdfs.length : ''}</span>}
      </button>
      {offen && (
        <div ref={box} data-testid="zeugnis-menue" role="dialog" aria-label="Zeugnis"
             className="fixed z-[60] w-[290px] bg-white rounded-xl border border-slate-200 shadow-xl p-3 text-sm normal-case"
             style={{ top: pos.top, left: pos.left }}>
          <div className="text-xs font-semibold text-slate-500 mb-2">Zeugnis · Position #{id}</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SCHNELL.map(s => (
              <button key={s.wert} type="button" disabled={busy} data-testid={`zeugnis-schnell-${s.wert}`} onClick={() => void speichern(s.wert)}
                      className={`px-2 py-1 rounded-md border text-xs font-semibold ${s.wert === 'fehlt' ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-emerald-200 text-emerald-800 hover:bg-emerald-50'}
                        ${(zeugnis ?? '').trim().toLowerCase() === s.wert ? 'ring-2 ring-slate-800' : ''}`}>{s.label}</button>
            ))}
          </div>
          <div className="flex gap-1.5 mb-3">
            <input data-testid="zeugnis-text" value={text} onChange={e => setText(e.target.value)} autoFocus
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void speichern(text); } }}
                   placeholder="frei, z. B. 3.1 lt. Lieferschein" className="flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1 text-sm" />
            <button type="button" data-testid="zeugnis-speichern" disabled={busy || text.trim() === (zeugnis ?? '').trim()} onClick={() => void speichern(text)}
                    className="px-2.5 rounded-md bg-slate-800 text-white text-xs font-semibold disabled:opacity-40">OK</button>
          </div>
          <div className="border-t border-slate-100 pt-2">
            {pdfs.map(n => (
              <a key={n} href={api.einkauf.dateiUrl(id, n)} target="_blank" rel="noreferrer"
                 className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-brand py-0.5 truncate" title={n}>📜 <span className="truncate">{n.replace(/^Zeugnis_/, '')}</span></a>
            ))}
            <label className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-brand cursor-pointer hover:underline">
              {busy ? 'lädt …' : '＋ Zeugnis-PDF hochladen'}
              <input type="file" accept="application/pdf,.pdf" multiple className="hidden" data-testid="zeugnis-pdf"
                     disabled={busy} onChange={e => { void hochladen(e.target.files); e.target.value = ''; }} />
            </label>
            {zeugnis && (
              <button type="button" disabled={busy} onClick={() => void speichern('')} className="float-right mt-1 text-xs text-slate-400 hover:text-red-600">Eintrag leeren</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
